import type { ReactSVGElement, SVGProps, VFC } from 'react';

import classNames from 'classnames';
import {
  Culture as CultureIcon,
  User as UserIcon,
  Video as VideoIcon,
  PlaceholderCircle as PlaceholderCircleIcon,
  Sport as SportIcon,
} from '@vgno/icons';

// Custom icons
import { ReactComponent as BrandsShape } from '../../icons/brands.svg';
import { ReactComponent as PlusShape } from '../../icons/plus.svg';
import { ReactComponent as UnknownShape } from '../../icons/unknown.svg';
import { ReactComponent as SchibstedShape } from '../../icons/schibsted-media.svg';
// Named icons for main menu
import { ReactComponent as NewsShape } from '../../icons/news-ab.svg';
import { ReactComponent as SportAbShape } from '../../icons/sport-ab.svg';
import { ReactComponent as GodareShape } from '../../icons/godare.svg';

import type { IconName } from './names';

import * as styles from './Icon.module.scss';

const createSvgIcon =
  (Shape: (props: SVGProps<SVGElement>) => ReactSVGElement) =>
  ({ className }: { className?: string }) => (
    <Shape className={classNames(styles.icon, className)} />
  );

export const PlusIcon = createSvgIcon(PlusShape);
export const SchibstedIcon = createSvgIcon(SchibstedShape);
export const BrandsIcon = createSvgIcon(BrandsShape);
export const UnknownIcon = createSvgIcon(UnknownShape);

// Named icons for main menu
const namedIcons: Record<IconName, VFC<any>> = {
  culture: CultureIcon,
  user: UserIcon,
  video: VideoIcon,
  football: SportIcon,
  sport: createSvgIcon(SportAbShape),
  news: createSvgIcon(NewsShape),
  godare: createSvgIcon(GodareShape),
} as const;

interface NamedIconProps {
  name: IconName;
  className?: string;
}

export const NamedIcon: VFC<NamedIconProps> = ({ name, className }) => {
  const IconComponent = namedIcons[name];

  if (!IconComponent) {
    console.warn(`Missing icon "${name}"`);
    return <PlaceholderCircleIcon className={className} />;
  }

  const iconClassName = classNames(styles.icon, className);

  return <IconComponent className={iconClassName} aria-label={name} />;
};
